import { Autocomplete, GoogleMap, InfoBox, InfoWindow, Marker, MarkerClusterer, MarkerProps, useLoadScript } from "@react-google-maps/api"
import React, { useState } from "react"
import { Button } from "../Button";
import { EMapTheme, ILinkFunc, getMapTheme } from "../../lib/util";
import { EWsrvTransform, IMediaItem } from "../../lib";
import { WsrvImage } from "../Media";

export interface IMapAgent {
    headshot?: IMediaItem;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    path?: { href: string; text: string; };
    locations: {
        lat: number;
        lng: number;
        name?: string;
        nameSmall?: string;
    }[];
}

export enum ESearchBoxLayout {
    autoHeight = "autoHeight",
    fullHeight = "fullHeight",
}

export interface IAgentMapSearchProps {
    agents: IMapAgent[];
    mapApiKey?: string;
    searchBoxLayout?: ESearchBoxLayout;
    className?: string;
    searchBoxClassName?: string;
    searchBoxTitle?: string;
    searchBoxTitleClassName?: string;
    searchBoxInputClassName?: string;
    searchBoxInputPlaceholder?: string;
    markerInfoWindowTitle?: string;
    markerInfoWindowClassName?: string;
    linkFunc?: ILinkFunc;
    switchToAlwaysOpened?: boolean;
    showMarkerLabel?: boolean;
    mapTheme?: EMapTheme;
    satelliteMode?: boolean;
    markerProps?: Partial<MarkerProps>;
    initialZoom?: number;
    initialCenter?: { lat: number; lng: number; };
}

export const AgentMapSearch: React.FC<IAgentMapSearchProps> = ({
    agents,
    mapApiKey = "AIzaSyBSypNhQTAdXM5ua2lx93n2Cwso-Rgra4w",
    searchBoxLayout = ESearchBoxLayout.fullHeight,
    className = "",
    searchBoxClassName = "",
    searchBoxTitle = "",
    searchBoxTitleClassName = "",
    searchBoxInputClassName = "",
    searchBoxInputPlaceholder,
    markerInfoWindowTitle,
    markerInfoWindowClassName = "",
    linkFunc,
    switchToAlwaysOpened = false,
    showMarkerLabel = false,
    mapTheme = EMapTheme.standard,
    markerProps,
    initialZoom = 3,
    initialCenter = { lat: 0, lng: 0 },
    satelliteMode = false,
}) => {

    const linkf = linkFunc || ((props) => <a {...props} />);

    const [searchOpened, setSearchOpened] = useState(switchToAlwaysOpened)

    let layoutSearchBoxClass = ""
    let layoutInputContainerClass = ""
    switch (searchBoxLayout) {
        case ESearchBoxLayout.autoHeight:
            layoutSearchBoxClass = `right-0 top-28 ${searchOpened ? "w-full" : ""} p-5 max-w-screen-sm`
            layoutInputContainerClass = "border-b"
            break
        default:
            layoutSearchBoxClass = `top-0 right-0 ${searchOpened ? "bottom-0 w-full" : ""} p-6 max-w-lg`
            layoutInputContainerClass = "border"
            break
    }

    const SearchIcon = () => (
        <svg width="38" height="38" viewBox="0 0 38 38" stroke="currentColor" strokeWidth={0} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.0312 4.09375C19.1504 4.09375 22.9082 6.33398 25.0039 9.875C27.0996 13.4883 27.0996 17.8965 25.0039 21.4375C22.9082 25.0508 19.1504 27.2188 15.0312 27.2188C10.8398 27.2188 7.08203 25.0508 4.98633 21.4375C2.89062 17.8965 2.89062 13.4883 4.98633 9.875C7.08203 6.33398 10.8398 4.09375 15.0312 4.09375ZM15.0312 30.6875C18.5 30.6875 21.752 29.5312 24.3535 27.5078L34.0371 37.1191C34.6875 37.8418 35.7715 37.8418 36.4219 37.1191C37.1445 36.4688 37.1445 35.3848 36.4219 34.7344L26.8105 25.0508C28.834 22.4492 30.0625 19.1973 30.0625 15.6562C30.0625 7.41797 23.2695 0.625 15.0312 0.625C6.7207 0.625 0 7.41797 0 15.6562C0 23.9668 6.7207 30.6875 15.0312 30.6875ZM20.8125 13.3438C20.8125 10.1641 18.2109 7.5625 15.0312 7.5625C11.7793 7.5625 9.25 10.1641 9.25 13.3438C9.25 16.8848 12.5742 21.4375 14.1641 23.3887C14.5977 23.8945 15.3926 23.8945 15.8262 23.3887C17.416 21.4375 20.7402 16.8848 20.7402 13.3438H20.8125ZM12.7188 13.3438C12.7188 12.1152 13.7305 11.0312 15.0312 11.0312C16.2598 11.0312 17.3438 12.1152 17.3438 13.3438C17.3438 14.6445 16.2598 15.6562 15.0312 15.6562C13.7305 15.6562 12.7188 14.6445 12.7188 13.3438Z" />
        </svg>
    )

    type Libraries = ("drawing" | "geometry" | "localContext" | "places" | "visualization")[];
    const [libraries] = useState<Libraries>(["places"])

    type AgentLocation = {
        headshot?: IMediaItem;
        firstName?: string;
        lastName?: string;
        fullName?: string;
        path?: { href: string; text: string; };
        location: {
            lat: number;
            lng: number;
            name?: string;
            nameSmall?: string;
        }
    }

    const agentsLocations: AgentLocation[] = []
    agents.forEach((agent) => {
        agent.locations.forEach(location => {
            agentsLocations.push({
                firstName: agent.firstName,
                lastName: agent.lastName,
                fullName: agent.fullName,
                path: agent.path,
                location: location,
                headshot: agent.headshot,
            })
        })
    })

    interface AgentMarkerProps extends Omit<MarkerProps, "position"> {
        agent: AgentLocation;
        keyIndex: number;
    }

    let theme = getMapTheme(mapTheme)

    const [gmap, setGMap] = useState<google.maps.Map | null>(null);
    const [latLngBounds, setLatLngBounds] = useState<any>(null);

    const defaultAutocomplete: any = null
    const [loadedAutocomplete, setAutocomplete] = useState(defaultAutocomplete)

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: mapApiKey,
        libraries,
    });

    const AgentMarker: React.FC<AgentMarkerProps> = (props) => {
        const [isInfoHidden, setIsInfoHidden] = useState<boolean>(true)
        const markerOnClickHandler = (marker: any) => {
            gmap?.setZoom(12)
            gmap?.setCenter(marker.latLng)
            setIsInfoHidden(false)
        }

        return (
            <Marker
                key={props.keyIndex}
                onClick={markerOnClickHandler}
                position={props.agent.location}
                title={props.agent.location.nameSmall ?? ""}
                label={showMarkerLabel && props.agent.location.nameSmall ? {
                    className: `bg-black/50 p-0.5`,
                    color: "white",
                    fontWeight: "700",
                    text: props.agent.location.nameSmall
                } : undefined}
                {...markerProps}
            >
                {!isInfoHidden && (
                    <InfoWindow
                        onCloseClick={() => { setIsInfoHidden(true) }}
                    >
                        <div className="p-2">
                            {markerInfoWindowTitle && (
                                <h3 className={`text-sm font-bold ${markerInfoWindowClassName} `}>{markerInfoWindowTitle}</h3>
                            )}
                            <div className="flex flew-row flex-nowrap justify-center">
                                <div className="grow min-w-[50%]">
                                    <p className="text-base font-bold">
                                        {(props.agent.firstName || props.agent.lastName) ? `${props.agent.firstName} ${props.agent.lastName} ` : props.agent.fullName}
                                    </p>
                                    <p>
                                        {props.agent.location.name ?? ""}
                                    </p>
                                </div>
                                {props.agent.headshot?.url && (
                                    <WsrvImage
                                        urlParams={{
                                            originURL: props.agent.headshot.url,
                                            preset: EWsrvTransform.thumb3x4
                                        }}
                                        imgProps={{
                                            className: "min-w-[50%] w-24",
                                            wrapperClassName: "block grow"
                                        }}
                                    />
                                )}
                            </div>
                            {props.agent.path?.href && <Button
                                linkProps={{
                                    className: "w-full mx-auto font-bold mt-3",
                                    href: props.agent.path?.href
                                }}
                                linkFunc={linkf}
                            >
                                {props.agent.path?.text}
                            </Button>}
                        </div>
                    </InfoWindow>
                )}
            </Marker>
        )
    }

    const renderMap = () => {

        const onLoad = (gmapInstance: google.maps.Map) => {
            let bounds = new google.maps.LatLngBounds();
            agentsLocations.map((item) => {
                bounds.extend(item.location);
            });
            setLatLngBounds(bounds)
            gmapInstance.setZoom(initialZoom)
            gmapInstance.setCenter(initialCenter)
            setGMap(gmapInstance)
        }
        return (
            <GoogleMap
                mapContainerClassName="w-full h-full"
                onLoad={onLoad}
                options={{
                    styles: theme,
                    mapTypeId: satelliteMode ? "hybrid" : "roadmap",
                }}
            >
                {isLoaded &&
                    agentsLocations?.map((agent, index) => (
                        <AgentMarker
                            key={index}
                            keyIndex={index}
                            agent={agent}
                        />
                    ))
                }
            </GoogleMap>
        )
    }

    const renderAutocomplete = () => {

        const changeLocationHandler = () => {
            if (loadedAutocomplete != null) {
                let location = loadedAutocomplete.getPlace()?.geometry?.location ?? null
                let newLat = location?.lat() ?? 0
                let newLng = location?.lng() ?? 0
                gmap?.setCenter({ lat: newLat, lng: newLng })
                gmap?.setZoom(9)
                setSearchOpened(false)
            }
        }
        const loadAutocompleteHandler = (autocomplete: any) => {
            setAutocomplete(autocomplete)
        }

        return (
            <Autocomplete
                className="grow"
                onLoad={loadAutocompleteHandler}
                bounds={latLngBounds}
                onPlaceChanged={changeLocationHandler}
            >
                <input
                    type="text"
                    name="location"
                    id="location"
                    placeholder={searchBoxInputPlaceholder ?? "Search for a location"}
                    required
                    className={`w-full px-2 bg-transparent text-2xl ${searchBoxInputClassName}`}
                />
            </Autocomplete>
        )
    }

    return (
        <section className={`relative w-full h-screen ${className}`}>
            {isLoaded && renderMap()}
            <div className={`bg-body-bg z-10 absolute shadow-lg flex flex-col ${layoutSearchBoxClass} ${searchBoxClassName} `}>
                {!switchToAlwaysOpened && (
                    <button type="button" className={`self-end text-4xl ${searchOpened ? "hidden" : ""}`} onClick={() => setSearchOpened(true)}>
                        <SearchIcon />
                    </button>
                )}
                {!switchToAlwaysOpened && <button type="button" className={`self-end text-4xl ${!searchOpened ? "hidden" : ""}`} onClick={() => { setSearchOpened(false) }}>&times;</button>}
                <h2 className={`text-center max-w-lg mx-auto ${!searchOpened ? "hidden" : ""} ${searchBoxTitleClassName}`}>{searchBoxTitle}</h2>
                <div className={`flex flex-row items-center w-full max-w-lg border-body-text my-11 mx-auto py-2.5 px-5 ${!searchOpened ? "hidden" : ""} ${layoutInputContainerClass} `}>
                    <SearchIcon />
                    {isLoaded && renderAutocomplete()}
                </div>
            </div>
        </section>
    )
}

export default AgentMapSearch